import { createSlice } from '@reduxjs/toolkit';
import { thunkApiCallGenerator } from '../APICalls/APICalls';

/* HELPER FUNCTIONS */

/** find differences between two quote versions
* @param prev obj
* @param current obj
* @param purpose string
* @return array
*/
export function diffSummary(prev, current, purpose){
    const historyEnums = {
        '1': 'Expiration Date was changed.',
        '2': 'Estimated Total Value was changed.',
        '3': 'Products were removed.',
        '4': 'Products were added.',
        '5': 'Discounts were changed.',
        '6': 'Machines were added.',
        '7': 'Machines were removed.',
        '8': 'Machine Rental duration was changed.'
    };
    const categoryEnums = {
        '1': 'Expiration Date',
        '2': 'Estimated Total Value',
        '3': 'Remove Item',
        '4': 'Add Item',
        '5': 'Discount',
        '6': 'Add Machine',
        '7': 'Remove Machine',
        '8': 'Machine Rental Duration'
    };
    const enums = purpose === 'history' ? historyEnums : categoryEnums;
    let changeEnums = [];

    /* check estimated total value and expiration date */
    if ( prev.generalInformation.activationDuration.expirationDate !== current.generalInformation.activationDuration.expirationDate ){
        changeEnums.push('1');
    }
    if ( prev.generalInformation.generalDetails.estimatedTotalValue !== current.generalInformation.generalDetails.estimatedTotalValue ){
        changeEnums.push('2');
    }
    /* check standard items. Two loops for symmetric difference (items removed vs. items added) */
    prev.productInformation.standardItems.forEach( prevItem => {
        let itemObj = current.productInformation.standardItems.find( currentItem => currentItem.productDescription === prevItem.productDescription );
        if ( itemObj ){
            if ( itemObj.discount !== prevItem.discount || itemObj.percentage !== prevItem.percentage ){ //item exists but discount was changed
                changeEnums.push('5');
            }
        } else { //item doesn't exist in current version, item was removed
            changeEnums.push('3');
        }
    });

    current.productInformation.standardItems.forEach( currentItem => {
        let itemObj = prev.productInformation.standardItems.find( prevItem => currentItem.productDescription === prevItem.productDescription );
        if ( !itemObj ){ //item was added
            changeEnums.push('4');
        }
    });

    /* check miscellaneous items. Two loops for symmetric difference (items removed vs. items added) */
    prev.productInformation.miscellaneousItems.forEach( prevItem => {
        let itemObj = current.productInformation.miscellaneousItems.find( currentItem => currentItem.productDescription === prevItem.productDescription );
        if ( itemObj ){
            if ( itemObj.totalPercentOff !== prevItem.totalPercentOff || itemObj.netPricing !== prevItem.netPricing ){ //item exists but discount was changed
                changeEnums.push('5');
            }
        } else { //item doesn't exist in current version, item was removed
            changeEnums.push('3');
        }
    });

    current.productInformation.miscellaneousItems.forEach( currentItem => {
        let itemObj = prev.productInformation.miscellaneousItems.find( prevItem => currentItem.productDescription === prevItem.productDescription );
        if ( !itemObj ){ //item was added
            changeEnums.push('4');
        }
    });

    /* check machine rentals. Two loops for symmetric difference (machines removed vs. machines added) */
    prev.machinesAndTools.forEach( prevMachine => {
        let machineObj = current.machinesAndTools.find( currentMachine => currentMachine.machineDescription === prevMachine.machineDescription );
        if ( machineObj ){
            if ( machineObj.rentalDuration !== prevMachine.rentalDuration ){ //machine exists but rental duration was changed
                changeEnums.push('8');
            }
        } else { //machine doesn't exist in current version, machine was removed
            changeEnums.push('7');
        }
    });

    current.machinesAndTools.forEach( currentMachine => {
        let machineObj = prev.machinesAndTools.find( prevMachine => currentMachine.machineDescription === prevMachine.machineDescription );
        if ( !machineObj ){ //machine was added
            changeEnums.push('6');
        }
    });

    changeEnums = [ ...new Set(changeEnums) ];

    return changeEnums.map( e => enums[e] );
}

/** Recursively deep copy an object or array
* @param object obj
* @return object
*/
export function deepCopy(obj){
	let newObject;
	if (obj === null || typeof obj !== "object"){
		return obj;
	}
	if (obj instanceof Array) {
        newObject = [];
        for (var i = 0; i < obj.length; i++) {
            newObject[i] = deepCopy( obj[i] );
        }
    } else if (obj instanceof Object) {
        newObject = {};
        for ( const key of Object.keys(obj) ) {
            newObject[key] = deepCopy( obj[key] );
        }
    }
    return newObject;
}

/** create mm/dd/yyyy timestamp
* @param object quoteObj
* @return string
*/
export function createTimeStamp(){
    let today = new Date();
    let year = today.getFullYear().toString();
    let day = today.getDate().toString();
    let month = (today.getMonth() + 1).toString();
    day = day.length < 2 ? '0' + day : day;
    month = month.length < 2 ? '0' + month : month;
    return month + '/' + day + '/' + year;
}

/** create mm/dd/yyyy hh:mm:ss timestamp for notifications
* @return string
*/
export function createNotificationTimeStamp(){
    let today = new Date();
    let year = today.getFullYear().toString();
    let day = today.getDate().toString();
    let month = (today.getMonth() + 1).toString();
    let hours = today.getHours().toString();
    let minutes = today.getMinutes().toString();
    let seconds = today.getSeconds().toString();
    day = day.length < 2 ? '0' + day : day;
    month = month.length < 2 ? '0' + month : month;
    hours = hours.length < 2 ? '0' + hours : hours;
    minutes = minutes.length < 2 ? '0' + minutes : minutes;
    seconds = seconds.length < 2 ? '0' + seconds : seconds;
    return month + '/' + day + '/' + year + ' ' + hours + ':' + minutes + ':' + seconds;
}

/** convert an ISO date string (yyyy-mm-dd) to mm/dd/yyyy format
* @param string isoString
* @return string
*/
export function isoToMonthDayYear(isoString){
    if( isoString ){
        const dateParts = isoString.split('-');
        if( dateParts.length === 3 ){
            let year = dateParts[0];
            let month = dateParts[1];
            let day = dateParts[2];
            return month + '/' + day + '/' + year;
        }
    }
    return false;
}

export function monthDayYearToIso(dateString){
    if( dateString ){
        const dateParts = dateString.split('/');
        if( dateParts.length === 3 ){
            let year = dateParts[2];
            let month = dateParts[0];
            let day = dateParts[1];
            return year + '-' + month + '-' + day;
        }
    }
    return "";
}

export function generateMinOrMaxDate(minimum){
    // let millis = Date.now();
    // let rangeMillis = 157680000000; //5 years in milliseconds
    if( minimum ){
        return '1990-01-01';
    } else {
        return '2100-01-01';
    }
    // let returnDate = new Date(millis);
    // let year = returnDate.getFullYear().toString();
    // let day = returnDate.getDate().toString();
    // let month = (returnDate.getMonth() + 1).toString();
    // day = day.length < 2 ? '0' + day : day;
    // month = month.length < 2 ? '0' + month : month;
    // return year + '-' + month + '-' + day;
}

export function createISOdateString(dateObj){
    if( dateObj instanceof Date ){
        let year = dateObj.getFullYear().toString();
        let day = dateObj.getDate();
        let month = (dateObj.getMonth() + 1);
        day = day < '10' ? '0'+day : day;
        month = month < '10' ? '0'+month : month;
        return year + '-' + month + '-' + day;
    }
    return false;
}

/** Loop through quote history and get most current revision.
* @param object quoteObj
* @return int
*/
export function getCurrentRevisionIndex(quoteObj){
    if( quoteObj.quote.length === 1 ){
        return 0;
    }
    let index = 0;
    let maxRevisionNumber = quoteObj.quote[0].revisionNumber;
    for( let i = 1; i < quoteObj.quote.length; i++ ){
        if( quoteObj.quote[i].revisionNumber > maxRevisionNumber ){
            maxRevisionNumber = quoteObj.quote[i].revisionNumber;
            index = i;
        }
    }
    return index;
}

/** Loop through quote array and get most current quote.
* @param array quotesArray
* @param string quoteId
* @return object
*/
export function findCurrentQuote(quotesArray, quoteId){
    let quoteObj;
    const relevantQuotes = quotesArray.filter( quote => quote.quoteId === quoteId );
    if( relevantQuotes.length > 1 ){
        let quoteInfoObjs = relevantQuotes.map( (quote, i) => {
            let date = new Date(quote.inputDate);
            return {
                dateVal: date.valueOf(),
                index: i
            };
        });
        let maxDate = quoteInfoObjs[0].dateVal;
        let maxIndex = quoteInfoObjs[0].index;
        quoteInfoObjs.forEach( infoObj => {
            if( maxDate < infoObj.dateVal ){
                maxDate = infoObj.dateVal;
                maxIndex = infoObj.index;
            }
        });
        quoteObj = relevantQuotes[ maxIndex ];
    } else {
        quoteObj = relevantQuotes[0];
    }
    return quoteObj;
}

/** Loop through quote array and filter quotes, only returning most recent quote per quoteId based on inputDate.
* @param array quotesArray
* @return array
*/
export function filterQuoteObjectsByInputDate(quotesArray){
    //console.log(quotesArray);
    let filteredQuotesArray = [];
    if( quotesArray.length > 0 ){
        let infoObjs = quotesArray.map( (quote, i) => {
            let date = quote.updatedAt? new Date(quote.updatedAt) : new Date(quote.inputDate);
            return {
                dateVal: date.valueOf(),
                quoteId: quote.quoteId,
                index: i
            }
        });
        let sorted = infoObjs.sort((a, b) => {
            return b.dateVal - a.dateVal;
        });
        //console.log(sorted);
        let filtered = [];
        filtered.push(sorted[0]);
        for( let i = 1; i < sorted.length; i++ ){
            if( sorted[i].quoteId !== sorted[i - 1].quoteId ){
                filtered.push(sorted[i]);
            }
        }
        filtered.forEach( obj => {
            filteredQuotesArray.push( quotesArray[ obj.index ] );
        });
    }
    console.log(filteredQuotesArray);
    return filteredQuotesArray;
}

export function userCanSuggest(quote, userId){
    const notificationObj = quote.notifications.find( notification => notification.userId === userId );
    const asmObj = quote.asms.find( asm => asm.userId === userId );
    if( notificationObj || asmObj ){ //both objects here just in case "suggest modification" is extended to another user Type. User will still need to be related to the quote.
        return true;
    }
    return false;
}

export function userCanApprove(quote, userId, role){
    //console.log(userId, role);
    let canApprove = false;
    quote.flags.forEach( flagObj => {
        if( flagObj.status && flagObj.status.toLowerCase() === 'pending' ){
            flagObj.chainOfCommand.forEach( command =>{
                if ( command.userId === userId && command.role === role ){
                    canApprove = true;
                }
            });
        }
    });
    return canApprove;
}

export function hasPendingFlags(flagsArray){
    let pendingFlags = false;
    flagsArray.forEach( flagObj => {
        if( flagObj.status.toLowerCase() === 'pending' ){
            pendingFlags = true;
        }
    });
    return pendingFlags;
}

export function hasUnfinishedFlags(flagsArray, credentials){
    let unfinishedFlags = false;
    const unfinishedFlagsArray = [];
    flagsArray.forEach( flagObj => {
        if( 
            flagObj.status.toString().toLowerCase() === 'approved' && //flag was approved
            flagObj.approvedBy.userName?.toLowerCase() === credentials.userName?.toLowerCase() && //flag approved by current user
            ( flagObj.approvedBy.proofOfAuthorization.trim() === '' || flagObj.approvedBy.proofOfAuthorization.trim().toLowerCase() === 'error' ) //no proof or there was an error uploading proof
        ){
            let userObj = flagObj.chainOfCommand.find( commandObj => commandObj.userId === credentials.userId ); //see if they are part of chain of command
            if( !userObj ){ //user not part of chain of command.
                unfinishedFlags = true;
                unfinishedFlagsArray.push( flagObj );
            }
        }
    });
    return {
        unfinishedFlags: unfinishedFlags,
        unfinishedFlagsArray: unfinishedFlagsArray
    };
}

function determineNotificationType(flagsArr){
    let notificationType;
    if( flagsArr.length === 0 ){ //quote status and notification status
        notificationType = "Approved";
    } else {
        const distinctFlagStatuses = [ ...new Set( flagsArr.map( flag => flag.status.toLowerCase() ) ) ];
        if( distinctFlagStatuses.includes('pending') ){
            notificationType = "Pending";
        } else {
            if( distinctFlagStatuses.includes('approved with change') ){
                notificationType = "Approved With Change";
            } else {
                notificationType = "Approved";
            }
        }
    }
    return notificationType;
}

/** Turn the Delegation of Authority requirements for a quote into flags
* The rules themselves live on the server (HDMI-Backend/doa/) and arrive via
* /api/delegationOfAuthority - the server re-checks them when the quote is
* saved, so they are never duplicated here.
* @param object quote
* @param array requiredFlags - { category, value, productDescription, flagDescription, role } from the server
* @param bool isRevision
* @return Object
*/
export function identifyAndCreateFlags(quote, requiredFlags, isRevision){
    //quoteObj is the object that will eventually be in the quote:[] array containing all quote details besides quoteId and quoteNumber
    const quoteObj = quote.quote[0];
    let rolesArray = [];
    let flags = [];
    let newFlags = []; // Track which flags are actually new (not preserved from previous revision)

    requiredFlags.forEach( required => {
        let newFlag = generateFlagObj( Math.floor( Math.random() * 9999999 ) ); //random number between 0 and 9999999
        newFlag.types.category = required.category; //category of flag, from modification categories
        newFlag.types.value = required.value;
        newFlag.types.meta.flagDescription = required.flagDescription;
        newFlag.types.meta.productDescription = required.productDescription;
        newFlag.chainOfCommand[0].role = required.role;
        flags.push( newFlag );
        newFlags.push( newFlag.id ); // Mark as new flag
    });

    if( isRevision === true ){
        quoteObj.flags.forEach( oldFlag => {
            let oldFlagStatus = oldFlag.status.toLowerCase();
            if( oldFlagStatus === 'approved' || oldFlagStatus === 'approved with change' ){
                const newFlagIndex = flags.findIndex( newFlag => {
                    let expression;
                    if( oldFlagStatus === 'approved' ){
                        expression = (
                            newFlag.types.category.toLowerCase() === oldFlag.types.category.toLowerCase() &&
                            newFlag.types.value === oldFlag.types.value &&
                            newFlag.types.meta.flagDescription.toLowerCase() === oldFlag.types.meta.flagDescription.toLowerCase() &&
                            newFlag.types.meta.productDescription.toLowerCase() === oldFlag.types.meta.productDescription.toLowerCase()
                        );
                    } else {
                        expression = (
                            newFlag.types.category.toLowerCase() === oldFlag.types.category.toLowerCase() &&
                            newFlag.types.value !== oldFlag.types.value &&
                            newFlag.types.meta.flagDescription.toLowerCase() === oldFlag.types.meta.flagDescription.toLowerCase() &&
                            newFlag.types.meta.productDescription.toLowerCase() === oldFlag.types.meta.productDescription.toLowerCase()
                        );
                    }
                    return expression;
                });
                if( newFlagIndex !== -1 ){
                    // Remove from newFlags since we're replacing with old flag
                    newFlags = newFlags.filter( id => id !== flags[newFlagIndex].id );
                    flags[ newFlagIndex ] = deepCopy(oldFlag);
                }
            } else if( oldFlagStatus === 'pending' ){
                // Also preserve pending flags that haven't changed to avoid duplicate notifications
                const newFlagIndex = flags.findIndex( newFlag => {
                    return (
                        newFlag.types.category.toLowerCase() === oldFlag.types.category.toLowerCase() &&
                        newFlag.types.value === oldFlag.types.value &&
                        newFlag.types.meta.flagDescription.toLowerCase() === oldFlag.types.meta.flagDescription.toLowerCase() &&
                        newFlag.types.meta.productDescription.toLowerCase() === oldFlag.types.meta.productDescription.toLowerCase()
                    );
                });
                if( newFlagIndex !== -1 ){
                    // Pending flag matches - preserve the old flag to avoid re-notification
                    // Remove from newFlags since we're replacing with old flag
                    newFlags = newFlags.filter( id => id !== flags[newFlagIndex].id );
                    flags[ newFlagIndex ] = deepCopy(oldFlag);
                }
            }
        });
        const modificationCategories = diffSummary(quote.quote[1], quoteObj, 'modifications').map( category => category.toLowerCase() );
        flags.forEach( flag => {
            console.log(flag);
            console.log(flag.status.toLowerCase());
            if( flag.status.toLowerCase() === 'approved with change' ){
                if( modificationCategories.includes( flag.types.category.toLowerCase() ) ){
                    flag.status = 'Approved';
                }
            }
        });
    }

    // Build rolesArray from new flags only (after preservation logic)
    // This ensures only users with genuinely new flags get notifications
    const newFlagsObjects = flags.filter( flag => newFlags.includes(flag.id) );
    const roleSet = new Set(); // Use Set to avoid duplicates

    newFlagsObjects.forEach( flag => {
        flag.chainOfCommand.forEach( command => {
            if( command.role ){
                // Determine userType from role
                let userType = '';
                if( command.role === 'Managing Director' ){
                    userType = 'MD';
                } else if( command.role === 'EPS' ){
                    userType = 'PM';
                } else if( command.role === 'Director Of Pricing' ){
                    userType = 'DM';
                }
                if( userType ){
                    roleSet.add( JSON.stringify({ app: 'hdmi', userType, role: command.role }) );
                }
            }
        });
    });

    // Convert Set back to array
    roleSet.forEach( roleStr => rolesArray.push( JSON.parse(roleStr) ) );

    // Always add Admin roles (for both new quotes and revisions with new flags)
    if( newFlags.length > 0 ){
        rolesArray.push({
            app: 'hdmi',
            userType: 'Admin',
            role: 'Super Admin'
        });
        rolesArray.push({
            app: 'hdmi',
            userType: 'Admin',
            role: 'Admin'
        });
    }
    const notificationType = determineNotificationType(flags);
    //const notificationType = "Approved";

    return { //return
        flagsArr: flags,
        rolesArr: rolesArray,
        notificationType: notificationType,
        newFlagsIds: newFlags // Return IDs of new flags
    };
}

/* */
function generateStandardItemObj(){
    return {
        itemType: 'Standard',
        productLine: '',
        productHierarchies: [],
        productDescription: '',
        matgr2: '',
        discountCode: '',
        stdDiscount: null,
        discount: '',
        percentage: '',
        multiplier: ''
    };
}

/* */
function generateMiscellaneousItemObj(){
    return {
        itemType: "Miscellaneous",
        productDescription: '',
        netPricing: '',
        totalPercentOff: ''
    };
}

/* */
function generateMachineRentalObj(){
    return {
        machineDescription: '',
        rentalDuration: ''
    };
}

/**  generate a flag object
* @param int flagId
* @return Object
*/
function generateFlagObj(flagId){
    const today = createTimeStamp(); //create mm/dd/yyyy timestamp
    return {
        id: flagId.toString(),
        types: {
            category: "",
            value: "",
            meta: {
                productDescription: "",
                flagDescription: ""
            }
        },
        status: "Pending",
        date: today,
        chainOfCommand: [
            {
                userId: "",
                userName: "",
                role: "",
                commandRank: '0'
            }
        ],
        approvedBy: {
            userName: "",
            date: "",
            role: "",
            proofOfAuthorization: ""
        }
    };
}

/* */
export function generateNotificationObj(type, role, userObj){
    const today = createNotificationTimeStamp();
    const uniqueId = Math.floor(Math.random() * 999999999); // Generate unique 9-digit ID
    return {
        notificationId: uniqueId.toString(),
        userId: userObj.userId,
        email: userObj.email,
        role: role,
        status: "Unread",
        Type: type,
        timestamp: today
    };
}

/** determine color of status text
* @param status string
* @return string
*/
export function quoteStatusClass(status){
    let className;
    switch(status.toLowerCase()){
        case 'approved':
            className = 'quote_status_approved';
        break;
        case 'pending':
            className = 'quote_status_pending';
        break;
        case 'approved with change':
            className = 'quote_status_approved_with_change';
        break;
        case 'denied':
            className = 'quote_status_denied';
        break;
        default:
            className = '';
    }
    return className;
}

/** remove leading zeros from customer number

*/

export function formatCustomerNumber(customerNumber){
    if(customerNumber.trim() !== ''){
        return Number(customerNumber).toString();
    } else {
        return customerNumber.trim();
    }
}

export const getCustomerInfo = thunkApiCallGenerator('quotesAndNotifications/getCustomerInfo');
export const getProductDescriptions = thunkApiCallGenerator('quotesAndNotifications/getProductDescriptions');
export const getDiscounts = thunkApiCallGenerator('quotesAndNotifications/getDiscounts');
export const getMachines = thunkApiCallGenerator('quotesAndNotifications/getMachines');
export const getQuotes = thunkApiCallGenerator('quotesAndNotifications/getQuotes');
export const putQuote = thunkApiCallGenerator('quotesAndNotifications/putQuote');
export const getNotifications = thunkApiCallGenerator('quotesAndNotifications/getNotifications');
export const getCategories = thunkApiCallGenerator('quotesAndNotifications/getCategories');
export const getASMs = thunkApiCallGenerator('quotesAndNotifications/getASMs');
export const getAllASMs = thunkApiCallGenerator('quotesAndNotifications/getAllASMs');
export const getMarkets = thunkApiCallGenerator('quotesAndNotifications/getMarkets');
export const putEmailNotification = thunkApiCallGenerator('quotesAndNotifications/putEmailNotification');
export const authorizationUpload = thunkApiCallGenerator('quotesAndNotifications/authorizationUpload');
export const printQuote = thunkApiCallGenerator('quotesAndNotifications/printQuote');
export const getAllNotifications = thunkApiCallGenerator('quotesAndNotifications/getAllNotifications');
export const checkQuoteNumber = thunkApiCallGenerator('quotesAndNotifications/checkQuoteNumber');
export const getAllQuotes = thunkApiCallGenerator('quotesAndNotifications/getAllQuotes');

const initialState = {
    activeQuoteInfo: {
        quoteId: null,
        revisionNumber: null,
        index: null
    },
    quotes: [],
    quoteFilters: {
        current: {
            customer: '',
            customerNumber: '',
            status: '',
            opportunityNumber: ''
        },
        previous: {
            customer: '',
            customerNumber: '',
            status: '',
            opportunityNumber: ''
        }
    },
    quoterList: [],
    quoteSearch: {
        quoteNumber: '',
        subject: '',
        quoter: '',
        opportunityNumber: ''
    },
    modification: {
        categories: [],
        content: ''
    },
    approveOnBehalf: {
        flags: [],
        files: []
    },
    unfinishedAuthUpload: {
        files: []
    },
    unsavedDenialReason: '',
    modificationCategories: [],
    customers: [],
    productDescriptions: [],
    discounts: [],
    machines: [],
    asms: [],
    markets: [],
    putQuoteReady: false,
    quotePrepared: false,
    uniqueQuoteNumber: true,
    createQuote: {
        app: "hdmi",
        quoteId: "",
        quoteNumber: "",
        quote: [
            {
                revisionNumber: '0',
                userId: "",
                userName: "",
                email: "",
                status: "",
                denialReason: "",
                timestamp: "",
                additionalNotes: "",
                generalInformation: {
                    customerInformation: {
                        customerNumber: "",
                        customer: "",
                        attn: "",
                        customerAddress: {
                            address: "",
                            city: "",
                            phone: "",
                            state: "",
                            zipcode: ""
                        }
                    },
                    activationDuration: {
                        activationDate: "",
                        expirationDate: ""
                    },
                    generalDetails: {
                        opportunityNumber: "",
                        estimatedTotalValue: "",
                        quoteSubject: "",
                        market: "",
                        asm: ""
                    }
                },
                productInformation: {
                    standardItems: [
                        generateStandardItemObj(),
                        generateStandardItemObj()
                    ],
                    miscellaneousItems: [
                        generateMiscellaneousItemObj(),
                        generateMiscellaneousItemObj()
                    ]
                },
                machinesAndTools: [
                    generateMachineRentalObj()
                ],
                asm: {
                    name: "",
                    SAP: "",
                    email: ""
                },
                flags: [],
                suggestions: [],
                notifications: [],
                modifications: []
            }
        ]
    },
    isQuoteRevision: false,
    machineEdit: false,
    stagedQuote: null,
    activeNotificationInfo: {
        quoteId: null,
        revisionNumber: null,
        notificationId: null
    },
    notificationFilters: {
        current: {
            status: '',
            type: ''
        },
        previous: {
            status: '',
            type: ''
        }
    },
    notificationSearch: {
        quoteNumber: '',
        subject: '',
        customer: ''
    },
    notifications: [],
    loadingStatus: {
        customerInfo: '',
        productDescriptions: '',
        discounts: '',
        machines: '',
        asms: '',
        markets: '',
        quotes: '',
        notifications: '',
        putQuote: '',
        getASMs: '',
        categories: '',
        putSuggestion: '',
        printQuote: ''
    }
};

export const quotesSlice = createSlice({
    name: 'quotesAndNotifications',
    initialState,

    reducers: {

        setLoadingStatus: (state, action) => {
            state.loadingStatus[ action.payload.field ] = action.payload.value;
        },

        clearQuote: (state) => {
            state.createQuote = deepCopy(initialState.createQuote);
            state.productDescriptions = [];
            state.discounts = [];
            state.machines = [];
            state.isQuoteRevision = false;
        },

        setCustomer: (state, action) => {
            if( action.payload.hasOwnProperty('customer') ){
                state.createQuote.quote[0].generalInformation.customerInformation.customer = action.payload.customer;
            }
            if( action.payload.hasOwnProperty('customerNumber') ){
                state.createQuote.quote[0].generalInformation.customerInformation.customerNumber = action.payload.customerNumber.replace(/[^0-9]+/g, "");
            }
            let customerAddress;
            if( action.payload.customerAddress ){
                customerAddress = deepCopy(action.payload.customerAddress);
            }
            if( customerAddress ){
                if( customerAddress.phone.trim() === '' ){
                    customerAddress.phone = "N/A";
                }
                state.createQuote.quote[0].generalInformation.customerInformation.customerAddress = customerAddress;
            } else {
                state.createQuote.quote[0].generalInformation.customerInformation.customerAddress = deepCopy( initialState.createQuote.quote[0].generalInformation.customerInformation.customerAddress );
            }
            state.createQuote.quote[0].productInformation = deepCopy(initialState.createQuote.quote[0].productInformation);
            state.createQuote.quote[0].machinesAndTools = deepCopy(initialState.createQuote.quote[0].machinesAndTools);
        },

        setActivationDate: (state, action) => {
            let value = action.payload ? isoToMonthDayYear(action.payload) : '';
            state.createQuote.quote[0].generalInformation.activationDuration.activationDate = value ? value : '';
        },

        setExpirationDate: (state, action) => {
            let value = action.payload ? isoToMonthDayYear(action.payload) : '';
            state.createQuote.quote[0].generalInformation.activationDuration.expirationDate = value ? value : '';
        },

        setOpportunityNumber: (state, action) => {
            let value = action.payload.replace(/[^\w]|_/g, '').toUpperCase();
            if (value && !value.startsWith('P')) {
                value = 'P' + value;
            }
            state.createQuote.quote[0].generalInformation.generalDetails.opportunityNumber = value;
        },

        setEstimatedTotalValue: (state, action) => {
            state.createQuote.quote[0].generalInformation.generalDetails.estimatedTotalValue = action.payload.replace(/[^0-9]+/g, '');
        },

        setQuoteNumber: (state, action) => {
            //state.createQuote.quoteNumber = action.payload.replace(/[^A-z0-9-.,'"+)(/$: ]+/g, '');
            state.createQuote.quoteNumber = action.payload.replace(/[^\w]|_/g, '').toUpperCase();
        },

        setQuoteSubject: (state, action) => {
            state.createQuote.quote[0].generalInformation.generalDetails.quoteSubject = action.payload.replace(/[^A-z0-9-.,'"+)(/$: ]+/g, '');
        },

        setMarket: (state, action) => {
            state.createQuote.quote[0].generalInformation.generalDetails.market = action.payload;
        },

        setASM: (state, action) => {
            // action.payload should be an object with { name, SAP, email }
            if (action.payload && action.payload.name) {
                state.createQuote.quote[0].generalInformation.generalDetails.asm = action.payload.name;
                state.createQuote.quote[0].asm = {
                    name: action.payload.name,
                    SAP: action.payload.SAP,
                    email: action.payload.email || ""
                };
            } else {
                // Clear both fields if payload is empty
                state.createQuote.quote[0].generalInformation.generalDetails.asm = "";
                state.createQuote.quote[0].asm = {
                    name: "",
                    SAP: "",
                    email: ""
                };
            }
        },

        setAttn: (state, action) => {
            state.createQuote.quote[0].generalInformation.customerInformation.attn = action.payload;
        },

        setStandardItem: (state, action) => {
            let discountObj;
            switch ( action.payload.field ){
                case 'productDescription':
                    const productObj = state.productDescriptions.find( product => product.productDescription === action.payload.value );
                    state.createQuote.quote[0].productInformation.standardItems[action.payload.index]['productDescription'] = action.payload.value;
                    if( productObj ){
                        state.createQuote.quote[0].productInformation.standardItems[action.payload.index]['productHierarchies'] = productObj['productHierarchies'];
                        state.createQuote.quote[0].productInformation.standardItems[action.payload.index]['productLine'] = productObj['productLine'] ? productObj['productLine'] : "";//productLine
                        state.createQuote.quote[0].productInformation.standardItems[action.payload.index]['matgr2'] = productObj['matgr2'] || ''; //material group the line is quoted under
                        state.createQuote.quote[0].productInformation.standardItems[action.payload.index]['discountCode'] = productObj['discountCode'];
                        state.createQuote.quote[0].productInformation.standardItems[action.payload.index]['stdDiscount'] = productObj['stdDiscount'];
                    } else {
                        state.createQuote.quote[0].productInformation.standardItems[action.payload.index]['productHierarchies'] = [];
                        state.createQuote.quote[0].productInformation.standardItems[action.payload.index]['productLine'] = '';
                        state.createQuote.quote[0].productInformation.standardItems[action.payload.index]['matgr2'] = '';
                        state.createQuote.quote[0].productInformation.standardItems[action.payload.index]['discountCode'] = '';
                        state.createQuote.quote[0].productInformation.standardItems[action.payload.index]['stdDiscount'] = null;
                    }
                break;
                case 'discount':
                    if( action.payload.value.match(/^(\d{1,2}(-\d{1,2})*|(\d{1,2}-){1,})$/g) ){
                        discountObj = state.discounts.find( discount => discount.discount === action.payload.value );
                        state.createQuote.quote[0].productInformation.standardItems[action.payload.index]['discount'] = action.payload.value;
                        if ( discountObj ){
                            state.createQuote.quote[0].productInformation.standardItems[action.payload.index]['percentage'] = discountObj.percentage.toString();
                            state.createQuote.quote[0].productInformation.standardItems[action.payload.index]['multiplier'] = discountObj.multiplier.toString();
                        } else {
                            if( action.payload.value.trim() !== '' ){
                                let discounts = action.payload.value.replace(/\s/g, '').split('-').map(discount => parseFloat( ( Number(discount) / 100 ).toFixed(2) ) );
                                let total = 100;
                                let totalPc = 0;
                                let currentPc;
                                discounts.forEach(discount => {
                                    currentPc = (total * Number(discount));
                                    total -= currentPc;
                                    totalPc += currentPc;
                                });
                                state.createQuote.quote[0].productInformation.standardItems[action.payload.index]['percentage'] = parseFloat(totalPc).toFixed(2);
                                state.createQuote.quote[0].productInformation.standardItems[action.payload.index]['multiplier'] = parseFloat( total / 100 ).toFixed(4);
                            } else {
                                state.createQuote.quote[0].productInformation.standardItems[action.payload.index]['percentage'] = '';
                                state.createQuote.quote[0].productInformation.standardItems[action.payload.index]['multiplier'] = '';
                            }
                        }
                    } else if( action.payload.value.trim() === '' ){
                        state.createQuote.quote[0].productInformation.standardItems[action.payload.index]['discount'] = '';
                        state.createQuote.quote[0].productInformation.standardItems[action.payload.index]['percentage'] = '';
                        state.createQuote.quote[0].productInformation.standardItems[action.payload.index]['multiplier'] = '';
                    }
                    
                break;
                case 'percentage':
                    if( action.payload.value.match(/^[0-9.%]+$/g) && action.payload.value.indexOf('.') === action.payload.value.lastIndexOf('.') && action.payload.value.indexOf('%') === action.payload.value.lastIndexOf('%') && action.payload.value !== '' && action.payload.value.length <= 6 ){
                        let percentString = action.payload.value;
                        discountObj = state.discounts.find( discount => discount.percentage.toString().replace(/%/g, '') === percentString.replace(/%/g, '') );
                        let percentSignIndex = percentString.indexOf('%');
                        if( percentSignIndex !== -1 && percentSignIndex !== ( percentString.length - 1 ) ){
                            percentString = percentString.replace(/%/g, '') + '%';
                        }
                        state.createQuote.quote[0].productInformation.standardItems[action.payload.index]['percentage'] = percentString;
                        if ( discountObj ){
                            state.createQuote.quote[0].productInformation.standardItems[action.payload.index]['discount'] = discountObj.discount;
                            state.createQuote.quote[0].productInformation.standardItems[action.payload.index]['multiplier'] = discountObj.multiplier.toString();

                        } else {
                            state.createQuote.quote[0].productInformation.standardItems[action.payload.index]['discount'] = '';
                            state.createQuote.quote[0].productInformation.standardItems[action.payload.index]['multiplier'] = parseFloat( ((100 - Number(percentString.replace(/%/g, ''))) / 100) ).toFixed(4);
                        }
                    } else if( action.payload.value.trim() === '' ) {
                        state.createQuote.quote[0].productInformation.standardItems[action.payload.index]['percentage'] = '';
                        state.createQuote.quote[0].productInformation.standardItems[action.payload.index]['discount'] = '';
                        state.createQuote.quote[0].productInformation.standardItems[action.payload.index]['multiplier'] = '';
                    }
                    
                break;
                case 'multiplier':
                    let multiplierString = action.payload.value.trim();
                    if( multiplierString.match(/^[0-9.]+$/g) && multiplierString.indexOf('.') === multiplierString.lastIndexOf('.') && multiplierString !== '' && multiplierString.length <= 6 ){
                        if( multiplierString[0] === '.' ){
                            multiplierString = '0'+multiplierString;
                        } else if( multiplierString[0] !== '0' ){
                            multiplierString = '0.'+multiplierString.replace('.', '');
                        } else if( multiplierString[0] === '0' && multiplierString.length > 1 && multiplierString.indexOf('.') === -1 ){
                            multiplierString = Number(multiplierString).toString();
                            multiplierString = '0.'+multiplierString;
                            if( multiplierString.length > 6 ){
                                multiplierString = multiplierString.substring(0,6);
                            }
                        }
                        discountObj = state.discounts.find( discount => discount.multiplier.toString() === multiplierString );
                        state.createQuote.quote[0].productInformation.standardItems[action.payload.index]['multiplier'] = multiplierString;
                        if ( discountObj ){
                            state.createQuote.quote[0].productInformation.standardItems[action.payload.index]['discount'] = discountObj.discount;
                            state.createQuote.quote[0].productInformation.standardItems[action.payload.index]['percentage'] = discountObj.percentage.toString().replace(/%/g, '') + '%';
                        } else {
                            state.createQuote.quote[0].productInformation.standardItems[action.payload.index]['discount'] = '';
                            state.createQuote.quote[0].productInformation.standardItems[action.payload.index]['percentage'] = parseFloat( parseFloat( 100 - ( Number(multiplierString) * 100 ) ).toFixed(2) ).toString() + '%';
                        }
                    } else if( multiplierString === '' ){
                        state.createQuote.quote[0].productInformation.standardItems[action.payload.index]['percentage'] = '';
                        state.createQuote.quote[0].productInformation.standardItems[action.payload.index]['discount'] = '';
                        state.createQuote.quote[0].productInformation.standardItems[action.payload.index]['multiplier'] = '';
                    }
                    
                break;
                // no default
            }
            let incompleteRows = false;
            const fieldsToCheck = [
                'percentage',
                'multiplier',
                'productDescription'
            ];
            state.createQuote.quote[0].productInformation.standardItems.forEach(item =>{
                for ( const field in item ) {
                    //if ( item.hasOwnProperty(field) && field !== 'discount' && field !== 'productLine' && field !== ( item[field] === null || item[field].toString().trim() === '') ){
                    if( fieldsToCheck.includes(field) && ( item[field] === null || item[field] === undefined || item[field].toString().trim() === '' ) ){
                        incompleteRows = true;
                    }
                }
            });

            if ( !incompleteRows ){
                state.createQuote.quote[0].productInformation.standardItems.push( generateStandardItemObj() );
            }

        },

        addStandardItem: (state) => {
            state.createQuote.quote[0].productInformation.standardItems.push( generateStandardItemObj() );
        },

        setMiscellaneousItem: (state, action) => {
            state.createQuote.quote[0].productInformation.miscellaneousItems[action.payload.index][action.payload.field] = action.payload.value;
            if ( action.payload.field === 'netPricing' ){
                state.createQuote.quote[0].productInformation.miscellaneousItems[action.payload.index]['totalPercentOff'] = '';
            } else if ( action.payload.field === 'totalPercentOff' ){
                state.createQuote.quote[0].productInformation.miscellaneousItems[action.payload.index]['netPricing'] = '';
            }

            let incompleteRows = false;

            state.createQuote.quote[0].productInformation.miscellaneousItems.forEach(item =>{
                if ( item.productDescription.trim() !== '' ){
                    if ( item.netPricing.trim() === '' && item.totalPercentOff.trim() === '' ){
                        incompleteRows = true;
                    }
                } else {
                    incompleteRows = true;
                }
            });

            if ( !incompleteRows ){
                state.createQuote.quote[0].productInformation.miscellaneousItems.push( generateMiscellaneousItemObj() );
            }
        },

        addMiscellaneousItem: (state) => {
            state.createQuote.quote[0].productInformation.miscellaneousItems.push( generateMiscellaneousItemObj() );
        },

        setMachineRental: (state, action) => {
            state.createQuote.quote[0].machinesAndTools[action.payload.index][action.payload.field] = action.payload.value;
        },

        setMachineRentalEdit: (state) => {
            state.machineEdit = !state.machineEdit;
        },

        clearMachineItem: (state, action) => {
            let machines = deepCopy(state.createQuote.quote[0].machinesAndTools);
            if( action.payload === 0 && machines.length === 1){
                machines = [ generateMachineRentalObj() ];
            } else if( machines.length > 1 ){
                machines = machines.filter( (_, i) => i !== action.payload );
            }
            state.createQuote.quote[0].machinesAndTools = machines;
        },

        addMachineRental: (state) => {
            state.createQuote.quote[0].machinesAndTools.push( generateMachineRentalObj() );
        },

        prepareQuote: (state, action) => {
            state.createQuote.quote[0].userId = action.payload.quoterId.toString();
            state.createQuote.quote[0].userName = action.payload.userName;
            state.createQuote.quote[0].email = action.payload.email;
            state.createQuote.quote[0].status = action.payload.quoteStatus;
            state.createQuote.quote[0].timestamp = createTimeStamp();
            state.createQuote.quote[0].productInformation.standardItems = state.createQuote.quote[0].productInformation.standardItems.filter( item => item.productDescription.trim() !== '' );
            state.createQuote.quote[0].productInformation.miscellaneousItems = state.createQuote.quote[0].productInformation.miscellaneousItems.filter( item => item.productDescription.trim() !== '' );
            state.createQuote.quote[0].machinesAndTools = state.createQuote.quote[0].machinesAndTools.filter(machine => machine.machineDescription.trim() !== '' );
        },

        setQuotePrepared: (state, action) => {
            state.quotePrepared = action.payload;
        },

        printQuoteJson: (state, action) => {
            //console.log("STRINGIFIED", JSON.stringify(state.createQuote));
        },

        openQuoteRevision: (state, action) => {
            state.isQuoteRevision = true;
            let quoteObj = deepCopy(action.payload.quote);
            //quoteObj.quote.unshift( deepCopy(quoteObj.quote[ getCurrentRevisionIndex(quoteObj) ]) ); // add new revision to quote
            quoteObj.quote.unshift( deepCopy(quoteObj.quote[ 0 ]) ); // add new revision to quote
            quoteObj.quote[0].revisionNumber = (Number(quoteObj.quote[0].revisionNumber) + 1).toString();
            quoteObj.quote[0].productInformation.standardItems.push( generateStandardItemObj() );
            quoteObj.quote[0].productInformation.miscellaneousItems.push( generateMiscellaneousItemObj() );
            if( quoteObj.quote[0].productInformation.standardItems.length < 2 ){
                quoteObj.quote[0].productInformation.standardItems.push( generateStandardItemObj() );
            }
            if( quoteObj.quote[0].productInformation.miscellaneousItems.length < 2 ){
                quoteObj.quote[0].productInformation.miscellaneousItems.push( generateMiscellaneousItemObj() );
            }
            if( quoteObj.quote[0].machinesAndTools.length === 0 ){
                quoteObj.quote[0].machinesAndTools.push( generateMachineRentalObj() );
            }
            state.createQuote = quoteObj;
        },

        setQuoteSearch: (state, action) => {
            state.quoteSearch[ action.payload.field ] = action.payload.value;
        },

        setQuoteFilters: (state, action) => {
            let customerObj;
            if ( action.payload.filter === 'customer' ){
                state.quoteFilters.current[ 'customer' ] = action.payload.value;
                if( action.payload.value === '' ){
                    state.quoteFilters.current[ 'customerNumber' ] = '';
                }else{
                    // customerObj = state.customers.find( obj => obj.customer === action.payload.value);
                    // if( customerObj ){
                    //     state.quoteFilters.current[ 'customerNumber' ] = customerObj.customerNumber;
                    // }
                }
            } else if ( action.payload.filter === 'customerNumber' ){
                if( action.payload.value === '' ){
                    state.quoteFilters.current[ 'customer' ] = '';
                    state.quoteFilters.current[ 'customerNumber' ] = '';
                } else if( action.payload.value.match(/^[0-9]+$/g) ) {
                    state.quoteFilters.current[ 'customerNumber' ] = action.payload.value;
                    if( action.payload.value.length > 5 ){
                        customerObj = state.customers.find( customer => {
                            //customer.customerNumber === action.payload.value
                            let startIndex = customer.customerNumber.length - action.payload.value.length; //get index to start comparison from in string since. we are comparing from right to left due to leading zeros in customer number
                            let endIndex = customer.customerNumber.length;
                            return ( customer.customerNumber.slice(startIndex, endIndex) === action.payload.value );
                        });
                    }
                    if( customerObj ){
                        state.quoteFilters.current[ 'customer' ] = customerObj.customer;
                        state.quoteFilters.current[ 'customerNumber' ] = customerObj.customerNumber;
                    }
                }
            } else if( action.payload.filter === 'status' ){
                state.quoteFilters.current[ 'status' ] = action.payload.value;
            } else if( action.payload.filter === 'opportunityNumber' ){
                state.quoteFilters.current[ 'opportunityNumber' ] = action.payload.value;
            }
        },

        setPreviousQuoteFilters: (state) => {
            state.quoteFilters.previous = deepCopy( state.quoteFilters.current );
        },

        setActiveQuoteInfo: (state, action) => {
            let activeQuoteInfo = deepCopy( state.activeQuoteInfo );
            if( activeQuoteInfo.quoteId !== action.payload.quoteId ){
                activeQuoteInfo = action.payload;
            } else if( action.payload.caller && action.payload.caller === 'history' ){ //data coming from history side panel

                if( action.payload.revisionNumber !== activeQuoteInfo.revisionNumber ){ //different revision number being viewed
                    activeQuoteInfo.revisionNumber = action.payload.revisionNumber;
                } else { //same revision number means history panel accordion was closed
                    const activeQuote = state.quotes.find( quote => quote.quoteId === activeQuoteInfo.quoteId );
                    //const currentRevisionNum = activeQuote.quote[ getCurrentRevisionIndex(activeQuote) ].revisionNumber;
                    const currentRevisionNum = activeQuote.quote[ 0 ].revisionNumber;
                    activeQuoteInfo.revisionNumber = currentRevisionNum;
                }

            } else if( action.payload.caller !== 'history' ) {
                activeQuoteInfo.quoteId = null;
                activeQuoteInfo.revisionNumber = null;
            }

            state.activeQuoteInfo = activeQuoteInfo;
            state.modification = deepCopy( initialState.modification );
            state.approveOnBehalf = deepCopy( initialState.approveOnBehalf );
            state.unsavedDenialReason = '';
        },

        setUnsavedDenialReason: (state, action) => {
            state.unsavedDenialReason = action.payload;
        },

        setNotificationFilters: (state, action) => {
            state.notificationFilters.current[ action.payload.filter ] = action.payload.value;
        },

        setPreviousNotificationFilters: (state, action) => {
            state.notificationFilters.previous = deepCopy( state.notificationFilters.current );
        },

        setNotificationSearch: (state, action) => {
            state.notificationSearch[ action.payload.field ] = action.payload.value;
        },

        setActiveNotificationInfo: (state, action) => {
            if( state.activeNotificationInfo.quoteId === action.payload.quoteId && state.activeNotificationInfo.revisionNumber === action.payload.revisionNumber ){
                if( state.activeNotificationInfo.notificationId === action.payload.notificationId ){
                    state.activeNotificationInfo.quoteId = null;
                    state.activeNotificationInfo.notificationId = null;
                } else {
                    state.activeNotificationInfo.notificationId = action.payload.notificationId;
                }
            } else {
                state.activeNotificationInfo.quoteId = action.payload.quoteId;
                state.activeNotificationInfo.revisionNumber = action.payload.revisionNumber;
                state.activeNotificationInfo.notificationId = action.payload.notificationId;
            }

            state.modification = deepCopy( initialState.modification );
            state.approveOnBehalf = deepCopy( initialState.approveOnBehalf );
            state.unsavedDenialReason = '';
        },

        setFlags: (state, action) => {
            state.createQuote.quote[0].flags = action.payload;
        },

        setModification: (state, action) => {
            if( action.payload.field === 'categories' ){
                if( state.modification.categories.includes( action.payload.value ) ){
                    state.modification.categories = state.modification.categories.filter( category => category !== action.payload.value );
                } else {
                    state.modification.categories = [...state.modification.categories, action.payload.value];
                }
            } else if( action.payload.field === 'content' ){
                state.modification.content = action.payload.value;
            } else if( action.payload.field === 'clear' ){
                state.modification = deepCopy(initialState.modification);
            }
        },

        setApproveOnBehalf: (state, action) => {
            let field =  action.payload.field;
            let value = action.payload.value;
            if( field === 'flags' ){
                let flags, flagObj, files;
                flagObj = state.approveOnBehalf.flags.find( flag => flag.id === value.id );
                if( flagObj ){
                    flags = state.approveOnBehalf.flags.filter( flag => flag.id !== value.id );
                    files = state.approveOnBehalf.files.filter( file => file.id !== value.id );
                    state.approveOnBehalf.files = files;
                } else {
                    flags = [...state.approveOnBehalf.flags, value];
                }
                state.approveOnBehalf.flags = flags;
            } else if( field === 'files' ){
                let fileObj, files;
                fileObj = state.approveOnBehalf.files.find( file => file.id === value.id );
                if( fileObj ){
                    files = state.approveOnBehalf.files.filter( file => file.id !== value.id );
                    files.push( value );
                } else {
                    files = [...state.approveOnBehalf.files, value];
                }
                state.approveOnBehalf.files = files;
            } else if( field === 'clear_files' ){
                let files = state.approveOnBehalf.files.filter( file => file.id !== value.id );
                state.approveOnBehalf.files = files;
            } else if( field === 'clear_all' ){
                state.approveOnBehalf = deepCopy(initialState.approveOnBehalf);
            }
        },

        setUnfinishedAuthUpload: (state, action) => {
            let fileArray = deepCopy(state.unfinishedAuthUpload.files);
            if( action.payload.actionType === 'add' ){
                let fileObj = state.unfinishedAuthUpload.files.find( file => file.id === action.payload.value.id );
                if( fileObj === undefined ){
                    fileArray.push( action.payload.value );
                }
            } else if( action.payload.actionType === 'clear' ) {
                fileArray = fileArray.filter( file => file.id !== action.payload.value.id );
            } else if( action.payload.actionType === 'clear_all' ){
                fileArray = [];
            }
            state.unfinishedAuthUpload.files = fileArray;
        },

        setASMS: (state, action) => {
            state.createQuote.quote[0].asms = action.payload;
        },

        setNotifications: (state, action) => {
            let notifications = [];
            const existingNotifications = state.createQuote.quote[0].notifications || [];

            // Create new notifications for all users in payload (they have new flags)
            action.payload.users.forEach((userObj) => {
                // Always create new notification - user is in payload because they have a new flag
                notifications.push(
                    generateNotificationObj(action.payload.type, userObj.role, userObj)
                );
            });

            // Preserve notifications for users NOT in the payload (they still have pending flags but no new ones)
            existingNotifications.forEach(existingNotif => {
                const isInPayload = action.payload.users.some(user =>
                    user.userId === existingNotif.userId && user.role === existingNotif.role
                );
                if (!isInPayload) {
                    // Preserve the entire notification including original notificationId
                    notifications.push(existingNotif);
                }
            });

            state.createQuote.quote[0].notifications = notifications;
            //console.log(notifications);
        },

        clearStateValues: (state) => {
            state.activeNotificationInfo = deepCopy(initialState.activeNotificationInfo);
            state.activeQuoteInfo = deepCopy(initialState.activeQuoteInfo);
            state.modification = deepCopy(initialState.modification);
            state.approveOnBehalf = deepCopy(initialState.approveOnBehalf);
            state.unsavedDenialReason = deepCopy(initialState.unsavedDenialReason);
            state.quoteFilters = deepCopy(initialState.quoteFilters);
            state.notificationFilters = deepCopy(initialState.notificationFilters);
            state.createQuote = deepCopy(initialState.createQuote);
            state.isQuoteRevision = false;
        },

        updateQuotesArray: (state, action) =>{
            const updateIndex = state.quotes.findIndex( quote => quote.quoteId === action.payload.quoteId );
            if( updateIndex !== -1 ){
                // Replace the element at the index - Immer will handle immutability
                state.quotes.splice(updateIndex, 1, action.payload);
            }
        },

        updateNotificationsArray: (state, action) =>{
            const updateIndex = state.notifications.findIndex( quote => quote.quoteId === action.payload.quoteId );
            if( updateIndex !== -1 ){
                // Replace the element at the index - Immer will handle immutability
                state.notifications.splice(updateIndex, 1, action.payload);
            }
        },

        setAdditionalNotes: (state, action) => {
            state.createQuote.quote[0].additionalNotes = action.payload;
        },

        setQuoterList: (state, action) => {
            state.quoterList = action.payload;
        },

        revisionReset: (state, action) => {
            let createQuoteArray = deepCopy(state.createQuote.quote);
            createQuoteArray[1].revisionNumber = (Number(createQuoteArray[1].revisionNumber) + 1).toString();
            createQuoteArray[1].productInformation.standardItems.push( generateStandardItemObj() );
            createQuoteArray[1].productInformation.miscellaneousItems.push( generateMiscellaneousItemObj() );
            if( createQuoteArray[1].productInformation.standardItems.length < 2 ){
                createQuoteArray[1].productInformation.standardItems.push( generateStandardItemObj() );
            }
            if( createQuoteArray[1].productInformation.miscellaneousItems.length < 2 ){
                createQuoteArray[1].productInformation.miscellaneousItems.push( generateMiscellaneousItemObj() );
            }
            if( createQuoteArray[1].machinesAndTools.length === 0 ){
                createQuoteArray[1].machinesAndTools.push( generateMachineRentalObj() );
            }
            state.createQuote.quote[0] = deepCopy(createQuoteArray[1]);
        }
    },

    extraReducers: (builder) => {
        builder.addCase(getCustomerInfo.pending, (state) => {

            state.loadingStatus.customerInfo = 'loading';

        }).addCase(getCustomerInfo.fulfilled, (state, action) => {

            state.loadingStatus.customerInfo = 'complete';
            let customers = action.payload.sort((a, b) => {
                return a.customer.localeCompare(b.customer);
            });
            state.customers = customers;

        }).addCase(getCustomerInfo.rejected, (state) => {

            state.loadingStatus.customerInfo = 'rejected';

        }).addCase(getProductDescriptions.pending, (state) => {

            state.loadingStatus.productDescriptions = 'loading';

        }).addCase(getProductDescriptions.fulfilled, (state, action) => { // object key used to be percentages
            
            state.loadingStatus.productDescriptions = 'complete';
            //console.log(action.payload);
            const filteredDescriptions = action.payload.products.filter( product => product.hasOwnProperty("productLine"));
            state.productDescriptions = filteredDescriptions;
            const parsedDiscounts = action.payload.discounts.map( discountObj => {
                let newDiscountObj = {...discountObj};
                if( !newDiscountObj.percentage.includes('%') ){ //percentage is in < 1 decimal format
                    if( newDiscountObj.percentage.length > 6 ){ //percentages with greater than 4 numbers after decimal have trailing 9s or 0s
                        newDiscountObj.percentage = parseFloat(newDiscountObj.percentage).toFixed(4); //round and truncate
                    }
                    newDiscountObj.percentage = parseFloat(newDiscountObj.percentage); //convert to number
                    if( newDiscountObj.percentage < 1 ){ //if percentage still in <1 decimal format, multiply by 100
                        newDiscountObj.percentage = newDiscountObj.percentage * 100;
                    }
                    newDiscountObj.percentage = parseFloat(newDiscountObj.percentage.toFixed(2)).toString() + '%'; //remove trailing zeros and convert back to string
                }
                if( newDiscountObj.multiplier[0] === '.' ){
                    newDiscountObj.multiplier = '0' + newDiscountObj.multiplier;
                }
                return newDiscountObj;
            });
            //console.log(parsedDiscounts);
            state.discounts = parsedDiscounts;

        }).addCase(getProductDescriptions.rejected, (state) => {

            state.loadingStatus.productDescriptions = 'rejected';

        }).addCase(getDiscounts.pending, (state) => {

            state.loadingStatus.discounts = 'loading';

        }).addCase(getDiscounts.fulfilled, (state, action) => {

            state.loadingStatus.discounts = 'complete';
            state.discounts = action.payload.discounts;
            //state.discounts = action.payload.discounts;

        }).addCase(getDiscounts.rejected, (state) => {

            state.loadingStatus.discounts = 'rejected';

        }).addCase(getMachines.pending, (state) => {

            state.loadingStatus.machines = 'loading';

        }).addCase(getMachines.fulfilled, (state, action) => {

            state.loadingStatus.machines = 'complete';
            state.machines = action.payload;
            if( action.payload.length === 1 ){
                if( state.createQuote.quote[0].machinesAndTools.length === 1 && state.createQuote.quote[0].machinesAndTools[0].machineDescription.trim() === '' ){
                    let estimatedTotalValue = state.createQuote.quote[0].generalInformation.generalDetails.estimatedTotalValue.replace(/[^0-9]/g, '');
                    estimatedTotalValue = Number(estimatedTotalValue);
    
                    if ( action.payload[0].rentalParameters.length === 1 && action.payload[0].rentalParameters[0].threshold <= estimatedTotalValue ){
                        state.createQuote.quote[0].machinesAndTools.machineDescription = action.payload[0].machineDescription;
                        state.createQuote.quote[0].machinesAndTools.rentalDuration = action.payload[0].rentalParameters[0].duration;
                    } else {
                        let thresholds = action.payload[0].rentalParameters.map( param => param.threshold);
                        thresholds = thresholds.sort((a, b) => a - b);
                        let qualifyingThreshold = -1;

                        for( let i = 0; i < thresholds.length; i++ ){
                            if( estimatedTotalValue >= thresholds[i] ){
                                qualifyingThreshold = thresholds[i];
                            }
                        }

                        if ( qualifyingThreshold !== -1 ){
                            let paramObject = action.payload[0].rentalParameters.find( paramObj => paramObj.threshold === qualifyingThreshold );
                            state.createQuote.quote[0].machinesAndTools.machineDescription = action.payload[0].machineDescription;
                            state.createQuote.quote[0].machinesAndTools.rentalDuration = paramObject.duration;
                        }
                    }
                }
            }

        }).addCase(getMachines.rejected, (state) => {

            state.loadingStatus.machines = 'rejected';

        }).addCase(getAllASMs.pending, (state) => {

            state.loadingStatus.asms = 'loading';

        }).addCase(getAllASMs.fulfilled, (state, action) => {

            state.loadingStatus.asms = 'complete';
            state.asms = action.payload || [];

        }).addCase(getAllASMs.rejected, (state) => {

            state.loadingStatus.asms = 'rejected';

        }).addCase(getMarkets.pending, (state) => {

            state.loadingStatus.markets = 'loading';

        }).addCase(getMarkets.fulfilled, (state, action) => {

            state.loadingStatus.markets = 'complete';
            state.markets = action.payload || [];

        }).addCase(getMarkets.rejected, (state) => {

            state.loadingStatus.markets = 'rejected';

        }).addCase(getQuotes.pending, (state) => {

            state.loadingStatus.quotes = 'loading';

        }).addCase(getQuotes.fulfilled, (state, action) => {//getAllQuotes
            
            let quotesArray = [];
            if( action.payload ){
                //quotesArray = deepCopy(action.payload).reverse();
                quotesArray = filterQuoteObjectsByInputDate(action.payload);
            }
            state.loadingStatus.quotes = 'complete';
            state.quotes = quotesArray;
            console.log(quotesArray);

        }).addCase(getQuotes.rejected, (state) => {

            state.loadingStatus.quotes = 'rejected';

        }).addCase(getAllQuotes.pending, (state) => {

            state.loadingStatus.quotes = 'loading';

        }).addCase(getAllQuotes.fulfilled, (state, action) => {//getAllQuotes

            let quotesArray = [];
            if( action.payload ){
                //quotesArray = deepCopy(action.payload).reverse();
                quotesArray = filterQuoteObjectsByInputDate(action.payload);
            }
            if( state.stagedQuote !== null ){ //quote or quote revision just created
                const foundQuoteIndex = quotesArray.findIndex( quote => quote.quoteId === state.stagedQuote.quoteId );
                if( foundQuoteIndex === -1 ){ //quote wasn't pulled, add it to quotes array
                    quotesArray.unshift( deepCopy(state.stagedQuote) );
                } else {
                    let existingRevNum = quotesArray[ foundQuoteIndex ].quote[0].revisionNumber;
                    let currentRevNum = state.stagedQuote.quote[0].revisionNumber;
                    if( existingRevNum !== currentRevNum ){ //old quote without newest revision pulled, add new version to quotes array
                        quotesArray[ foundQuoteIndex ] = deepCopy(state.stagedQuote);
                    }
                }
                state.stagedQuote = null;
            }
            state.loadingStatus.quotes = 'complete';
            state.quotes = quotesArray;
            // console.log(quotesArray);

        }).addCase(getAllQuotes.rejected, (state) => {

            state.loadingStatus.quotes = 'rejected';

        }).addCase(getNotifications.pending, (state) => {

            state.loadingStatus.notifications = 'loading';

        }).addCase(getNotifications.fulfilled, (state, action) => {
            //console.log("payload",action.payload);
            state.loadingStatus.notifications = 'complete';
            const copy = deepCopy(action.payload);
            const noteArr = filterQuoteObjectsByInputDate(action.payload);
            //console.log("noteArr",noteArr);
            state.notifications = noteArr ? noteArr : [];

        }).addCase(getNotifications.rejected, (state) => {

            state.loadingStatus.notifications = 'rejected';

        }).addCase(putQuote.pending, (state) => {
            state.quotePrepared = false;
            state.loadingStatus.putQuote = 'loading';

        }).addCase(putQuote.fulfilled, (state, action) => {
            if( action.payload[0].quote[0].generalInformation.customerInformation.customerNumber === action.meta.arg.quote[0].generalInformation.customerInformation.customerNumber ){
                state.loadingStatus.putQuote = 'complete';
                state.stagedQuote = deepCopy(action.payload[0]);
            } else {
                state.loadingStatus.putQuote = 'error';
            }

        }).addCase(putQuote.rejected, (state) => {

            state.loadingStatus.putQuote = 'rejected';

        }).addCase(getCategories.pending, (state) => {

            state.loadingStatus.categories = 'loading';

        }).addCase(getCategories.fulfilled, (state, action) => {

            state.loadingStatus.categories = 'complete';
            state.modificationCategories = action.payload.categories;

        }).addCase(getCategories.rejected, (state) => {

            state.loadingStatus.categories = 'rejected';

        }).addCase(getASMs.pending, (state) => {

            state.loadingStatus.putQuote = 'loading';

        }).addCase(getASMs.fulfilled, (state, action) => {

            //state.loadingStatus.putQuote = 'complete';
            state.createQuote.quote[0].asms = action.payload;

        }).addCase(getASMs.rejected, (state) => {

            state.loadingStatus.putQuote = 'rejected';

        }).addCase(getAllNotifications.pending, (state) => {

            state.loadingStatus.notifications = 'loading';

        }).addCase(getAllNotifications.fulfilled, (state, action) => {
            //console.log(action.payload);
            state.loadingStatus.notifications = 'complete';
            const copy = deepCopy(action.payload);
            const noteArr = filterQuoteObjectsByInputDate(action.payload);
            state.notifications = noteArr ? noteArr : [];

        }).addCase(getAllNotifications.rejected, (state) => {

            state.loadingStatus.notifications = 'rejected';

        }).addCase(checkQuoteNumber.pending, (state) => {
            //console.log("pending");

        }).addCase(checkQuoteNumber.fulfilled, (state, action) => {
            //console.log("complete", action.payload);
            state.uniqueQuoteNumber = action.payload;

        }).addCase(checkQuoteNumber.rejected, (state) => {

            state.uniqueQuoteNumber = false;

        })

    },//checkQuoteNumber
});

export const {
    setQuoteSearch,
    setQuoteFilters,
    setPreviousQuoteFilters,
    setCustomerNumber,
    setCustomer,
    setActivationDate,
    setExpirationDate,
    setOpportunityNumber,
    setEstimatedTotalValue,
    setQuoteSubject,
    setMarket,
    setASM,
    setAttn,
    setStandardItem,
    addStandardItem,
    setMiscellaneousItem,
    addMiscellaneousItem,
    setMachineRental,
    setMachineRentalEdit,
    addMachineRental,
    setNotificationSearch,
    setNotificationFilters,
    setPreviousNotificationFilters,
    setActiveNotificationInfo,
    setActiveQuoteInfo,
    openQuoteRevision,
    prepareQuote,
    setASMS,
    setFlags,
    setNotifications,
    printQuoteJson,
    setModification,
    setUnsavedDenialReason,
    setApproveOnBehalf,
    setUnfinishedAuthUpload,
    clearStateValues,
    setQuotePrepared,
    setLoadingStatus,
    clearQuote,
    setQuoteNumber,
    updateQuotesArray,
    updateNotificationsArray,
    clearMachineItem,
    setAdditionalNotes,
    setQuoterList,
    revisionReset
} = quotesSlice.actions;

/* Notifications Selectors */
export const selectNotificationFilters = state => state.quotesAndNotifications.notificationFilters;
export const selectNotifications = state => state.quotesAndNotifications.notifications;
export const selectActiveNotificationInfo = state => state.quotesAndNotifications.activeNotificationInfo;
export const selectActiveNotificationQuote = state => state.quotesAndNotifications.notifications.find( notifiedQuote => notifiedQuote.quoteId === state.quotesAndNotifications.activeNotificationInfo.quoteId );
export const selectNotificationSearch = state => state.quotesAndNotifications.notificationSearch;

/* View Quotes Selectors */
export const selectQuoteFilters = state => state.quotesAndNotifications.quoteFilters;
export const selectQuotes = state => state.quotesAndNotifications.quotes;
export const selectActiveQuoteInfo = state => state.quotesAndNotifications.activeQuoteInfo;
export const selectQuoteSearch = state => state.quotesAndNotifications.quoteSearch;
export const selectQuoterList = state => state.quotesAndNotifications.quoterList;

/* Create Quote Selectors */
export const selectGeneralInformation = state => state.quotesAndNotifications.createQuote.quote[0].generalInformation;
export const selectCustomerInformation = state => state.quotesAndNotifications.createQuote.quote[0].generalInformation.customerInformation;
export const selectActivationDuration = state => state.quotesAndNotifications.createQuote.quote[0].generalInformation.activationDuration;
export const selectGeneralDetails = state => state.quotesAndNotifications.createQuote.quote[0].generalInformation.generalDetails;
export const selectProductInformation = state => state.quotesAndNotifications.createQuote.quote[0].productInformation;
export const selectMachinesAndTools = state => state.quotesAndNotifications.createQuote.quote[0].machinesAndTools;
export const numStdItems = state => state.quotesAndNotifications.createQuote.quote[0].productInformation.standardItems.length;
export const numMiscItems = state => state.quotesAndNotifications.createQuote.quote[0].productInformation.miscellaneousItems.length;
export const selectCompleteQuote = state => state.quotesAndNotifications.createQuote;
export const selectMachineEdit = state => state.quotesAndNotifications.machineEdit;
export const selectDiscounts = state => state.quotesAndNotifications.discounts;
export const selectProductDescriptions = state => state.quotesAndNotifications.productDescriptions;
export const selectMachines = state => state.quotesAndNotifications.machines;
export const selectASMs = state => state.quotesAndNotifications.asms;
export const selectMarkets = state => state.quotesAndNotifications.markets;
export const selectIsQuoteRevision = state => state.quotesAndNotifications.isQuoteRevision;
export const selectQuotePrepared = state => state.quotesAndNotifications.quotePrepared;
export const selectUniqueQuoteNumber = state => state.quotesAndNotifications.uniqueQuoteNumber;

/* Multi Feature Selectors */
export const selectCustomers = state => state.quotesAndNotifications.customers;
export const selectModificationCategories = state => state.quotesAndNotifications.modificationCategories;
export const selectModification = state => state.quotesAndNotifications.modification;
export const selectActiveQuote = state => state.quotesAndNotifications.quotes.find( quote => quote.quoteId === state.quotesAndNotifications.activeQuoteInfo.quoteId );
export const selectUnsavedDenialReason = state => state.quotesAndNotifications.unsavedDenialReason;
export const selectApproveOnBehalf = state => state.quotesAndNotifications.approveOnBehalf;
export const selectLoadingStatus = state => state.quotesAndNotifications.loadingStatus;
export const selectUnfinishedAuthUpload = state => state.quotesAndNotifications.unfinishedAuthUpload;

export default quotesSlice.reducer;